import React, { useEffect, useState } from 'react';
import { Drawer, Spin, Alert, Tag, Typography, Descriptions, Space, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import { getAdminBookingMoney, IAdminBookingMoney, IAdminMoneyEvent } from '../../services/AdminService';
import { useAppBreakpoint } from '../../hooks/useAppBreakpoint';
import {
  MONEY_LOCATION_LABEL_KEYS,
  MONEY_LOCATION_COLORS,
  CAPTURE_MODE_LABEL_KEYS,
  DEPOSIT_COVERAGE_LABEL_KEYS,
  DEPOSIT_COVERAGE_COLORS,
  MONEY_EVENT_LABEL_KEYS,
  REFUND_SOURCE_LABEL_KEYS,
} from '../../constants/adminMoneyOptions';
import { SURFACE_1_SOFT, TEAL, CORAL } from '../../theme/creamyTokens';
import BookingReferenceTag from '../BookingReferenceTag';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';

const { Title, Text } = Typography;

interface IBookingMoneyDrawerProps {
  bookingId?: string;
  onClose: () => void;
}

const money = (amount: number | undefined | null, currency: string): string =>
  amount == null ? '—' : `${amount.toFixed(2)} ${currency}`;

/**
 * One booking's money, start to finish.
 *
 * The question this answers is the one that used to need seven tables open
 * at once: where is every cent of this booking right now, and how did it get
 * there. It is read-only on purpose -- moving money is never a panel action,
 * and a screen that can only look is a screen nobody can break.
 */
const BookingMoneyDrawer: React.FC<IBookingMoneyDrawerProps> = ({ bookingId, onClose }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { isMobile } = useAppBreakpoint();
  const [data, setData] = useState<IAdminBookingMoney | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!bookingId) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(false);
    getAdminBookingMoney(bookingId)
      .then(setData)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [bookingId]);

  const section = (title: string, children: React.ReactNode) => (
    <div style={{ marginBottom: 20 }}>
      <Title level={5} style={{ marginBottom: 8 }}>
        {title}
      </Title>
      {children}
    </div>
  );

  const renderEvent = (event: IAdminMoneyEvent, index: number, currency: string) => {
    const labelKey = MONEY_EVENT_LABEL_KEYS[event.kind];
    return (
      <div
        key={`${event.kind}-${event.at}-${index}`}
        style={{
          display: 'flex',
          gap: 10,
          padding: '8px 0',
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
          // A forecast is not a fact. Dimming it is the whole difference
          // between "the hold lapsed" and "the hold lapses on Thursday".
          opacity: event.isForecast ? 0.55 : 1,
        }}
      >
        <div
          style={{
            width: 8,
            height: 8,
            borderRadius: 999,
            marginTop: 6,
            flexShrink: 0,
            background: event.isForecast ? token.colorTextQuaternary : event.amount != null ? TEAL : token.colorTextTertiary,
          }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
            <Text strong style={{ fontSize: 13 }}>
              {labelKey ? translate(t, labelKey) : event.kind}
            </Text>
            {event.amount != null && (
              <Text style={{ fontSize: 13, whiteSpace: 'nowrap' }}>{money(event.amount, event.currency ?? '')}</Text>
            )}
          </div>
          <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
            {new Date(event.at).toLocaleString()}
          </Text>
          {event.detail && (
            <Text type="secondary" style={{ fontSize: 12, display: 'block', overflowWrap: 'anywhere' }}>
              {event.detail}
            </Text>
          )}
          {event.reference && (
            <Text type="secondary" style={{ fontSize: 11, display: 'block', overflowWrap: 'anywhere' }}>
              {event.reference}
            </Text>
          )}
        </div>
      </div>
    );
  };

  return (
    <Drawer
      title={translate(t, TRANSLATIONS.ADMIN_MONEY_TITLE)}
      open={bookingId !== undefined}
      onClose={onClose}
      width={isMobile ? '100%' : 560}
    >
      {loading || (!data && !error) ? (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin />
        </div>
      ) : error || !data ? (
        <Alert type="error" showIcon message={translate(t, TRANSLATIONS.ADMIN_MONEY_LOAD_ERROR)} />
      ) : (
        <>
          <div
            style={{
              background: SURFACE_1_SOFT,
              borderRadius: 14,
              padding: 14,
              marginBottom: 20,
            }}
          >
            <Text strong style={{ display: 'block', fontSize: 15 }}>
              {data.machineryTitle}
            </Text>
            <BookingReferenceTag reference={data.bookingReference} copyable style={{ display: 'block' }} />
            <Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 10 }}>
              {data.hostName} → {data.guestName}
            </Text>
            <Space size={6} wrap>
              <Tag color={MONEY_LOCATION_COLORS[data.moneyLocation]} style={{ marginInlineEnd: 0 }}>
                {translate(t, MONEY_LOCATION_LABEL_KEYS[data.moneyLocation])}
              </Tag>
              <Tag style={{ marginInlineEnd: 0 }}>
                {translate(t, CAPTURE_MODE_LABEL_KEYS[data.captureMode] ?? data.captureMode)}
              </Tag>
              <Tag style={{ marginInlineEnd: 0 }}>{data.paymentStatus}</Tag>
            </Space>
          </div>

          {/* Loud, because every proportional split in this app divides by
              the line sum: while it disagrees with the total, refunds,
              commission and transfers all come out wrong. */}
          {!data.linesMatchTotal && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message={translate(t, TRANSLATIONS.ADMIN_MONEY_LINES_MISMATCH, {
                lines: money(data.linesTotal, data.hostCurrencyCode),
                total: money(data.totalPrice, data.hostCurrencyCode),
              })}
            />
          )}

          {section(
            translate(t, TRANSLATIONS.ADMIN_MONEY_SECTION_AMOUNTS),
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_MONEY_TOTAL)}>
                {money(data.totalPrice, data.hostCurrencyCode)}
              </Descriptions.Item>
              {data.refundedAmount > 0 && (
                <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_MONEY_REFUNDED)}>
                  <Text style={{ color: CORAL }}>-{money(data.refundedAmount, data.hostCurrencyCode)}</Text>
                </Descriptions.Item>
              )}
              <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_MONEY_NET)}>
                <Text strong>{money(data.netCharged, data.hostCurrencyCode)}</Text>
              </Descriptions.Item>
              <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_MONEY_PLATFORM_FEE)}>
                {money(data.platformFee, data.hostCurrencyCode)}
              </Descriptions.Item>
              <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_MONEY_HOST_PAYOUT)}>
                {money(data.hostPayoutAmount, data.hostCurrencyCode)}
              </Descriptions.Item>
              {/* Only worth a row when the two currencies actually differ --
                  otherwise it is the same number said twice. */}
              {data.guestCurrencyCode !== data.hostCurrencyCode && (
                <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_MONEY_GUEST_CHARGE)}>
                  {money(data.guestChargeAmount, data.guestCurrencyCode)}{' '}
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    ({translate(t, TRANSLATIONS.ADMIN_MONEY_EXCHANGE_RATE)} {data.exchangeRate})
                  </Text>
                </Descriptions.Item>
              )}
              <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_MONEY_SAVED_CARD)}>
                {translate(t, data.hasSavedCard ? TRANSLATIONS.ADMIN_MONEY_SAVED_CARD_YES : TRANSLATIONS.ADMIN_MONEY_SAVED_CARD_NO)}
              </Descriptions.Item>
              {data.paymentReference && (
                <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_MONEY_PAYMENT_REF)}>
                  <Text style={{ fontSize: 12, overflowWrap: 'anywhere' }}>{data.paymentReference}</Text>
                </Descriptions.Item>
              )}
              {data.stripeConnectedAccountId && (
                <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_MONEY_CONNECTED_ACCOUNT)}>
                  <Text style={{ fontSize: 12, overflowWrap: 'anywhere' }}>{data.stripeConnectedAccountId}</Text>
                </Descriptions.Item>
              )}
              {data.fundsReleasedToHostAt && (
                <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_MONEY_RELEASED_AT)}>
                  {new Date(data.fundsReleasedToHostAt).toLocaleString()}{' '}
                  {data.fundsReleaseReason && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      ({data.fundsReleaseReason})
                    </Text>
                  )}
                </Descriptions.Item>
              )}
              {data.overtimeAmountBilled != null && (
                <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_MONEY_OVERTIME)}>
                  {money(data.overtimeAmountBilled, data.hostCurrencyCode)}{' '}
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    ({data.overtimeChargeStatus})
                  </Text>
                </Descriptions.Item>
              )}
              {data.hostDepositCharged > 0 && (
                <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_MONEY_HOST_DEPOSIT_CHARGED)}>
                  {money(data.hostDepositCharged, data.hostCurrencyCode)}
                </Descriptions.Item>
              )}
              {data.disputeResolvedAmount != null && (
                <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_MONEY_DISPUTE)}>
                  {money(data.disputeResolvedAmount, data.hostCurrencyCode)}{' '}
                  {!data.disputeSettledAt && (
                    <Tag color="red" style={{ marginInlineStart: 6 }}>
                      {translate(t, TRANSLATIONS.ADMIN_MONEY_DISPUTE_STUCK)}
                    </Tag>
                  )}
                </Descriptions.Item>
              )}
            </Descriptions>
          )}

          {section(
            translate(t, TRANSLATIONS.ADMIN_MONEY_SECTION_DEPOSIT),
            data.deposit.amount == null || data.deposit.amount <= 0 ? (
              <Text type="secondary">{translate(t, TRANSLATIONS.ADMIN_DEPOSIT_NONE)}</Text>
            ) : (
              <>
                {data.deposit.authorizationFailed && (
                  <Alert
                    type="error"
                    showIcon
                    style={{ marginBottom: 10 }}
                    message={translate(t, TRANSLATIONS.ADMIN_DEPOSIT_AUTH_FAILED)}
                  />
                )}
                <Descriptions column={1} bordered size="small">
                  <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_DEPOSIT_AMOUNT)}>
                    {money(data.deposit.amount, data.hostCurrencyCode)}
                  </Descriptions.Item>
                  <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_DEPOSIT_STATUS)}>
                    {data.deposit.status}
                  </Descriptions.Item>
                  {/* The one row that matters on a long rental: the hold is
                      usually already dead and the saved card is what is
                      really standing behind the machine. */}
                  <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_DEPOSIT_COVERAGE)}>
                    <Tag color={DEPOSIT_COVERAGE_COLORS[data.deposit.coverage]} style={{ marginInlineEnd: 0 }}>
                      {translate(t, DEPOSIT_COVERAGE_LABEL_KEYS[data.deposit.coverage] ?? data.deposit.coverage)}
                    </Tag>
                  </Descriptions.Item>
                  {data.deposit.holdExpiresAt && (
                    <Descriptions.Item
                      label={translate(
                        t,
                        data.deposit.holdExpired ? TRANSLATIONS.ADMIN_DEPOSIT_HOLD_EXPIRED : TRANSLATIONS.ADMIN_DEPOSIT_HOLD_EXPIRES,
                      )}
                    >
                      <Text style={{ color: data.deposit.holdExpired ? CORAL : undefined }}>
                        {new Date(data.deposit.holdExpiresAt).toLocaleString()}
                      </Text>
                    </Descriptions.Item>
                  )}
                  {data.deposit.releaseScheduledAt && (
                    <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_DEPOSIT_RELEASE_SCHEDULED)}>
                      {new Date(data.deposit.releaseScheduledAt).toLocaleString()}
                    </Descriptions.Item>
                  )}
                  {data.deposit.releasedAt && (
                    <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_DEPOSIT_RELEASED_AT)}>
                      {new Date(data.deposit.releasedAt).toLocaleString()}
                    </Descriptions.Item>
                  )}
                  {data.deposit.paymentIntentId && (
                    <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_MONEY_PAYMENT_REF)}>
                      <Text style={{ fontSize: 12, overflowWrap: 'anywhere' }}>{data.deposit.paymentIntentId}</Text>
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </>
            ),
          )}

          {section(
            translate(t, TRANSLATIONS.ADMIN_MONEY_SECTION_LINES),
            data.lines.length === 0 ? (
              <Text type="secondary">{translate(t, TRANSLATIONS.ADMIN_MONEY_NO_LINES)}</Text>
            ) : (
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                {data.lines.map((line) => (
                  <div
                    key={line.id}
                    style={{
                      border: `1px solid ${token.colorBorderSecondary}`,
                      borderRadius: 12,
                      padding: '10px 12px',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                      <Text strong style={{ fontSize: 13 }}>
                        {line.title}
                      </Text>
                      <Text style={{ fontSize: 13 }}>{money(line.amount, line.hostCurrencyCode)}</Text>
                    </div>
                    <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                      {line.providerName || line.lineType}
                    </Text>
                    <Space size={4} wrap style={{ marginTop: 6 }}>
                      <Tag
                        color={line.paymentStatus === 'Paid' ? 'green' : line.paymentStatus === 'PendingResolution' ? 'red' : 'default'}
                        style={{ marginInlineEnd: 0 }}
                      >
                        {line.paymentStatus}
                      </Tag>
                      {line.transferAttempts > 0 && <Tag style={{ marginInlineEnd: 0 }}>{line.transferAttempts}</Tag>}
                    </Space>
                    {line.transferLastErrorMessage && (
                      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4, overflowWrap: 'anywhere' }}>
                        {line.transferLastErrorMessage}
                      </Text>
                    )}
                  </div>
                ))}
              </Space>
            ),
          )}

          {section(
            translate(t, TRANSLATIONS.ADMIN_MONEY_SECTION_REFUNDS),
            <>
              {/* Only money handed back before the ledger existed can land
                  here, and a timeline that hid it would look complete. */}
              {data.hasUnloggedRefunds && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 10 }}
                  message={translate(t, TRANSLATIONS.ADMIN_MONEY_UNLOGGED_REFUNDS)}
                />
              )}
              {data.refunds.length === 0 ? (
                <Text type="secondary">{translate(t, TRANSLATIONS.ADMIN_MONEY_NO_REFUNDS)}</Text>
              ) : (
                <Space direction="vertical" size={6} style={{ width: '100%' }}>
                  {data.refunds.map((refund) => (
                    <div key={refund.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                      <div style={{ minWidth: 0 }}>
                        <Text strong style={{ fontSize: 13 }}>
                          {translate(t, REFUND_SOURCE_LABEL_KEYS[refund.source] ?? refund.source)}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                          {new Date(refund.createdAt).toLocaleString()}
                          {refund.issuedByName ? ` · ${refund.issuedByName}` : ''}
                        </Text>
                        {refund.reason && (
                          <Text type="secondary" style={{ fontSize: 12, display: 'block', overflowWrap: 'anywhere' }}>
                            {refund.reason}
                          </Text>
                        )}
                      </div>
                      <Text strong style={{ fontSize: 13, whiteSpace: 'nowrap', color: CORAL }}>
                        -{money(refund.amount, refund.currencyCode)}
                      </Text>
                    </div>
                  ))}
                </Space>
              )}
            </>,
          )}

          {section(
            translate(t, TRANSLATIONS.ADMIN_MONEY_SECTION_INVOICES),
            data.invoices.length === 0 ? (
              <Text type="secondary">{translate(t, TRANSLATIONS.ADMIN_MONEY_NO_INVOICES)}</Text>
            ) : (
              <Space direction="vertical" size={6} style={{ width: '100%' }}>
                {data.invoices.map((invoice) => (
                  <div key={invoice.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                    <div style={{ minWidth: 0 }}>
                      <Text strong style={{ fontSize: 13 }}>
                        {invoice.invoiceNumber}
                      </Text>
                      <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                        {invoice.issuerName} → {invoice.recipientName} · {new Date(invoice.issuedAt).toLocaleDateString()}
                      </Text>
                    </div>
                    <Text style={{ fontSize: 13, whiteSpace: 'nowrap' }}>{money(invoice.amount, invoice.currency)}</Text>
                  </div>
                ))}
              </Space>
            ),
          )}

          {section(
            translate(t, TRANSLATIONS.ADMIN_MONEY_SECTION_TIMELINE),
            <div>{data.timeline.map((event, index) => renderEvent(event, index, data.hostCurrencyCode))}</div>,
          )}
        </>
      )}
    </Drawer>
  );
};

export default BookingMoneyDrawer;
